import React, { useEffect, useState } from 'react'
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  FloatingPortal,
} from '@floating-ui/react'
import { useModalState } from 'src/Modal/ModalStateContext'
import { MenuContainer, MenuContent } from './FloatingMenu.styles'
import { FLOATING_MENU_PLACEMENT } from './FloatingMenu.constants'

export const FloatingMenu = ({
  isOpen,
  onClose,
  children,
  menuRef,
  anchorElement, // New optional prop for anchor element
  placement = FLOATING_MENU_PLACEMENT.RIGHT_START, // Default placement
  offsetDistance = 8, // Default offset distance
  onClick,
}: {
  isOpen: boolean
  onClose: () => void
  children: React.ReactNode
  menuRef: React.MutableRefObject<HTMLDivElement | null>
  anchorElement?: Element | null
  placement?: FLOATING_MENU_PLACEMENT
  offsetDistance?: number
  onClick?: () => void
}): JSX.Element | null => {
  const [isPositioned, setIsPositioned] = useState(false)
  const { activeModal } = useModalState()

  // Use Floating UI for positioning
  const { refs, floatingStyles } = useFloating({
    open: isOpen,
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [offset(offsetDistance), flip()],
  })

  // Set the anchor element if provided
  useEffect(() => {
    if (anchorElement) {
      refs.setReference(anchorElement)
      setIsPositioned(true)
    } else {
      setIsPositioned(false)
    }
  }, [anchorElement, refs])

  // Reset positioning state when menu closes
  useEffect(() => {
    if (!isOpen) {
      setIsPositioned(false)
    }
  }, [isOpen])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (activeModal) return
      const clickedElement = event.target as Node
      const isClickInsideMenu =
        menuRef.current && menuRef.current.contains(clickedElement)
      const isClickInsideAnchor =
        anchorElement && anchorElement.contains(clickedElement)
      if (!isClickInsideMenu && !isClickInsideAnchor) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose, menuRef, anchorElement, activeModal])

  // Only render when open AND positioned
  if (!isOpen || !isPositioned) return null

  return (
    <FloatingPortal>
      <MenuContainer
        ref={(node) => {
          // Set both the floating ref and the menuRef
          refs.setFloating(node)
          if (menuRef.current !== node) {
            menuRef.current = node
          }
        }}
        isOpen={isOpen}
        style={floatingStyles} // Use Floating UI positioning
        onClick={onClick}
      >
        <MenuContent>{children}</MenuContent>
      </MenuContainer>
    </FloatingPortal>
  )
}
